import { Button } from '@cocoonspace/ui/components/button'
import { Separator } from '@cocoonspace/ui/components/separator'
import { cn } from '@cocoonspace/ui/lib/utils'
import { useLingui } from '@lingui/react/macro'
import {
	CalendarIcon,
	CheckIcon,
	ClockIcon,
	type LucideIcon,
} from 'lucide-react'

import {
	type SpaceVisitStep,
	useSpaceVisitStepper,
} from './space-visit-stepper.context'

const iconRefs: Record<SpaceVisitStep, LucideIcon> = {
	day: CalendarIcon,
	intro: CalendarIcon,
	payment: CheckIcon,
	startTime: ClockIcon,
}

const StepBtn = ({
	step,
	activeStep,
}: {
	step: SpaceVisitStep
	activeStep: SpaceVisitStep
}) => {
	const { t } = useLingui()
	const { setStep } = useSpaceVisitStepper()

	const disabled =
		(step === 'startTime' && activeStep === 'day') ||
		(step === 'payment' && ['day', 'startTime'].includes(activeStep))

	const labelRefs: Record<SpaceVisitStep, string> = {
		day: t`Date`,
		intro: '',
		payment: t`Confirmation`,
		startTime: t`Créneau`,
	}

	const Icon = iconRefs[step]

	return (
		<Button
			className='relative !size-10 p-0'
			disabled={disabled}
			onClick={() => setStep(step)}
			variant={activeStep === step ? 'default' : 'outline'}
		>
			<Icon />

			<span
				className={cn(
					'text-foreground disabled:text-foreground/50 absolute -bottom-5 left-1/2 -translate-x-1/2 text-xs font-normal',
					{ 'text-primary': activeStep === step },
				)}
			>
				{labelRefs[step]}
			</span>
		</Button>
	)
}

export const StepperIndicator = () => {
	const { step: activeStep } = useSpaceVisitStepper()

	if (activeStep === 'intro') return null

	return (
		<div className='my-2 flex items-center gap-4 pb-4'>
			<StepBtn
				activeStep={activeStep}
				step='day'
			/>

			<Separator
				className={cn('flex-1', {
					'bg-foreground': ['startTime', 'payment'].includes(activeStep),
				})}
			/>

			<StepBtn
				activeStep={activeStep}
				step='startTime'
			/>

			<Separator
				className={cn('flex-1', {
					'bg-foreground': activeStep === 'payment',
				})}
			/>

			<StepBtn
				activeStep={activeStep}
				step='payment'
			/>
		</div>
	)
}
